/* eslint-disable @typescript-eslint/no-explicit-any */
import { ExportData } from 'model/reports/export/ExportData';
import _ from 'lodash';
import { ResponseByCountResult } from 'model/reports/ResponseByCountResult';
import { StatisticsResults } from 'model/reports/StatisticsResults';
import { GridResults } from 'model/reports/GridResults';
import { getPercentForGrid, getPercentLabelForGrid } from 'helpers/results';
import { RankResults } from 'model/reports/RanksData';
import htmlToPdfMake from 'html-to-pdfmake';
import { ListQuestionOptions } from 'model/reports/ListResults';
import { getTemplateText } from '@ax2/common/helpers/template';
import TemplateContent from '@ax2/common/model/schemas/TemplateContent';
import checkBox from 'assets/check_box.png';
import radio from 'assets/circle-lined.png';
import textArea from 'assets/text-area.jpg';
import numericInput from 'assets/numeric-input.jpg';
import textField from 'assets/text-field.jpg';

export const exportAsPDF = async (
  data: ExportData[],
  name: string
): Promise<void> => {
  const pdfMake = (await import('pdfmake/build/pdfmake')).default;
  const pdfFonts = (await import('pdfmake/build/vfs_fonts')).default;
  pdfMake.vfs = pdfFonts.pdfMake.vfs;

  const docDefinition = {
    info: {
      title: `${name} Survey Results`,
      author: 'Eversana',
    },
    pageSize: 'A4',
    content: _.map(data, (d, index) => {
      switch (d.type) {
        case 'image':
          return getChartReportContent(d, index);
        case 'responseByCountTable':
          return getResponseTable(d, index);
        case 'statisticsTable':
          return getStatisticsResponseTable(d, index);
        case 'gridTable':
          return getGridTable(d, index);
        case 'RanksTable':
          return getRanksResponseTable(d, index);
        case 'ColumnRows':
          return getColumnRows(d, index);
        case 'RatingBar':
          return getRatingOptions(d, index);
        case 'ListOptions':
          return getListOptions(d, index);
        case 'TextBoxField':
          return getTextBoxOptions(d, index);
        default:
          throw Error(
            `Unknown Export Type (${d.type}) was used in export builder.`
          );
      }
    }),
    styles: getStyles(),
    pageBreakBefore: function(
      currentNode: any,
      followingNodesOnPage: any,
      _nodesOnNextPage: any,
      _previousNodesOnPage: any
    ) {
      return (
        currentNode.headlineLevel === 1 && followingNodesOnPage.length === 0
      );
    },
  };
  pdfMake.createPdf(docDefinition as any).download(`${name} Results.pdf`);
};

const getStyles = () => ({
  title: {
    fontSize: 12,
    bold: true,
    marginBottom: 5,
  },
  text: {
    fontSize: 10,
  },
  tableElement: {
    fontSize: 10,
  },
  header: {
    bold: true,
    fontSize: 10,
  },
  subHeader: {
    bold: true,
    fontSize: 10,
    color: 'gray',
  },
  table: {
    margin: [0, 5, 0, 15],
  },
  listOptions: {
    margin: [0, 3, 0, 3],
    fontSize: 11,
  },
  helperText: {
    fontSize: 10,
    italics: true,
    color: 'gray',
  },
});

const getChartReportContent = (content: ExportData, index: number) =>
  getQuestionReport(index, content.text, [
    {
      image: content.data as string,
      width: 500,
    },
    '\n\n',
  ]);

const getResponseTable = (content: ExportData, index: number) => {
  const report = content.data as ResponseByCountResult;
  return getQuestionReport(index, content.text, [
    {
      style: 'table',
      table: {
        headerRows: 1,
        widths: ['auto', '*'],
        body: _.concat(
          [
            [
              {
                text: 'Count',
                style: 'header',
              },
              {
                text: 'Response',
                style: 'header',
              },
            ],
          ],
          _.map(report, r => [
            {
              text: r.count,
              style: 'tableElement',
            },
            {
              text: r.response,
              style: 'tableElement',
            },
          ])
        ),
      },
      layout: {
        hLineWidth: function(i: number, node: any) {
          return i === 0 || i === node.table.widths.length + 2 ? 0 : 0.25;
        },
        vLineWidth: function(_i: any, _node: any) {
          return 0;
        },
        hLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
        vLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
      },
    },
    '\n\n',
  ]);
};

const getStatisticsResponseTable = (content: ExportData, index: number) => {
  const report = content.data as StatisticsResults;
  return getQuestionReport(index, content.text, [
    {
      style: 'table',
      table: {
        headerRows: 1,
        widths: ['*', 'auto', 'auto', 'auto', 'auto'],
        body: _.concat(
          [
            [
              {
                text: 'Item',
                style: 'header',
              },
              {
                text: 'Average',
                style: 'header',
              },
              {
                text: 'Min',
                style: 'header',
              },
              {
                text: 'Max',
                style: 'header',
              },
              {
                text: 'Total Responses',
                style: 'header',
              },
            ],
          ],
          _.map(report.labels, (l, index) => [
            {
              text: l,
              style: 'tableElement',
            },
            {
              text: report.data[index].avg,
              style: 'tableElement',
            },
            {
              text: report.data[index].min,
              style: 'tableElement',
            },
            {
              text: report.data[index].max,
              style: 'tableElement',
            },
            {
              text: report.data[index].totalResponses,
              style: 'tableElement',
            },
          ])
        ),
      },
      layout: {
        hLineWidth: function(i: number, node: any) {
          return i === 0 || i === node.table.widths.length + 2 ? 0 : 0.25;
        },
        vLineWidth: function(_i: any, _node: any) {
          return 0;
        },
        hLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
        vLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
      },
    },
    '\n\n',
  ]);
};

const getRanksResponseTable = (content: ExportData, index: number) => {
  const report = content.data as RankResults;
  return getQuestionReport(index, content.text, [
    {
      style: 'table',
      table: {
        headerRows: 1,
        widths: [100, ...Array(report.labels.length).fill('*')],
        body: _.concat(
          [
            _.concat(
              [
                {
                  text: 'Answer Options',
                  style: 'header',
                  alignment: 'center',
                },
              ],
              _.map(report.labels, (_, i) => ({
                text: (i + 1).toString(),
                style: 'header',
                alignment: 'center',
              }))
            ),
          ],
          _.map(report.labels, (rl, rowIdx) =>
            _.concat(
              [
                {
                  text: [
                    {
                      text: rl,
                      style: 'tableElement',
                      alignment: 'left',
                    },
                    {
                      text: `\nCount (Column %)`,
                      style: 'subHeader',
                      alignment: 'left',
                    },
                  ],
                },
              ] as any[],
              _.map(report.labels, (_cl, columnIdx) => {
                const value = _.find(
                  report.data,
                  d => d.order === columnIdx && d.index === rowIdx
                );
                const data = value
                  ? _.find(
                      report.countData,
                      d =>
                        d.data.index === value.index &&
                        d.data.order === value.order
                    )
                  : null;
                const text = `${data ? data.count : 0} \n(${
                  data ? data.percent : 0
                }%)`;
                return {
                  text: text,
                  style: 'tableElement',
                  alignment: 'center',
                };
              })
            )
          )
        ),
      },
      layout: {
        hLineWidth: function(i: number, node: any) {
          return i === 0 || i === node.table.widths.length ? 0 : 0.25;
        },
        vLineWidth: function(i: number, _node: any) {
          return i === 1 ? 0.25 : 0;
        },
        hLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
        vLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
      },
    },
    '\n\n',
  ]);
};

const getGridTable = (content: ExportData, index: number) => {
  const report = content.data as GridResults;
  return getQuestionReport(index, content.text, [
    {
      style: 'table',
      table: {
        headerRows: 1,
        widths: Array(report.columnLabels.length + 1).fill('*'),
        body: _.concat(
          [
            _.concat(
              [
                {
                  text: '',
                  style: 'header',
                  alignment: 'center',
                },
              ],
              _.map(report.columnLabels, cl => ({
                text: cl,
                style: 'header',
                alignment: 'center',
              }))
            ),
          ],
          _.map(report.rowLabels, (rl, rowIdx) =>
            _.concat(
              [
                {
                  text: [
                    {
                      text: rl,
                      style: 'tableElement',
                      alignment: 'left',
                    },
                    {
                      text: `\nCount (${getPercentLabelForGrid(
                        report.requiredStrategy
                      )} %)`,
                      style: 'subHeader',
                      alignment: 'left',
                    },
                  ],
                },
              ] as any[],
              _.map(report.columnLabels, (_cl, columnIdx) => {
                const data = _.find(
                  report.data,
                  d => d.rowIdx === rowIdx && d.columnIdx === columnIdx
                );
                const text = data
                  ? `${data.count} (${getPercentForGrid(
                      report.requiredStrategy,
                      data
                    )}%)`
                  : 'Not found';
                return {
                  text: text,
                  style: 'tableElement',
                  alignment: 'center',
                };
              })
            )
          )
        ),
      },
      layout: {
        hLineWidth: function(i: number, node: any) {
          return i === 0 || i === node.table.widths.length ? 0 : 0.25;
        },
        vLineWidth: function(i: number, _node: any) {
          return i === 1 ? 0.25 : 0;
        },
        hLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
        vLineColor: function(_i: any, _node: any) {
          return 'lightgray';
        },
      },
    },
    '\n\n',
  ]);
};

const getTextBoxOptions = (content: ExportData, index: number) => {
  const report = content.data as ListQuestionOptions;
  const qType = report.qType;
  const numericType = qType.includes('OpenEndedNumeric');
  return getQuestionReport(
    index,
    content.text,
    [
      {
        style: 'table',
        table: {
          headerRows: 1,
          widths: ['auto'],
          body: [
            [
              {
                stack: [
                  {
                    image: numericType ? numericInput : textArea,
                    width: numericType ? 55 : 200,
                    height: numericType ? 25 : 100,
                    margin: [-5, 0, 0, 2],
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function(_i: number, _node: any) {
            return 0;
          },
          vLineWidth: function(_i: any, _node: any) {
            return 0;
          },
          hLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
          vLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
        },
      },
      '\n\n',
    ],
    report
  );
};

const getListOptions = (content: ExportData, index: number) => {
  const report = content.data as ListQuestionOptions;
  const qType = report.qType.toLowerCase();
  const radioType = qType.includes('radio');
  const checkboxType = qType.includes('checkbox');
  const numericType = qType.includes('numeric') || qType.includes('sumlist');
  const textBoxType = qType.includes('textboxlisttext');
  return getQuestionReport(
    index,
    content.text,
    [
      {
        style: 'table',
        table: {
          headerRows: 1,
          widths: ['auto'],
          body: [
            [
              {
                stack: [
                  {
                    type:
                      radioType || checkboxType || numericType || textBoxType
                        ? 'none'
                        : 'default',
                    ul: _.map(report.options, (op: any) => {
                      const content: TemplateContent = op.content;
                      return {
                        columns: [
                          radioType ||
                          checkboxType ||
                          numericType ||
                          textBoxType
                            ? {
                                image: radioType
                                  ? radio
                                  : checkboxType
                                  ? checkBox
                                  : numericType
                                  ? numericInput
                                  : textBoxType
                                  ? textField
                                  : null,
                                width: numericType
                                  ? 35
                                  : textBoxType
                                  ? 200
                                  : 10,
                                height: numericType
                                  ? 17
                                  : textBoxType
                                  ? 25
                                  : 10,
                                margin: [
                                  -15,
                                  numericType ? 0 : textBoxType ? 2 : 4,
                                  0,
                                  2,
                                ],
                              }
                            : {},
                          {
                            text: textBoxType ? '' : getTemplateText(content),
                            margin: [-6, 2, 0, 2],
                          },
                        ],
                      };
                    }),
                    style: 'listOptions',
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function(_i: number, _node: any) {
            return 0;
          },
          vLineWidth: function(_i: any, _node: any) {
            return 0;
          },
          hLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
          vLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
        },
      },
      '\n\n',
    ],
    report
  );
};

const getColumnRows = (content: ExportData, index: number) => {
  const report = content.data as ListQuestionOptions;
  const reportColumns = report.columns || [];
  const qType = report.qType.toLowerCase();
  const radioType = qType.includes('radio');
  const checkboxType = qType.includes('checkbox');
  const textType = qType.includes('text') || qType.includes('sumlist');
  return getQuestionReport(
    index,
    content.text,
    [
      {
        style: 'table',
        table: {
          headerRows: 1,
          widths: Array(reportColumns.length + 1).fill('*'),
          body: _.concat(
            [
              _.concat(
                [
                  {
                    text: '',
                    style: 'header',
                    alignment: 'center',
                  },
                ],
                _.map(report.columns, (c: any) => {
                  const content: TemplateContent = c.content;
                  return {
                    text: getTemplateText(content),
                    style: 'header',
                    alignment: 'center',
                  };
                })
              ),
            ],
            _.map(report.rows, (r: any) => {
              const content: TemplateContent = r.content;
              return _.concat(
                [
                  {
                    text: [
                      {
                        text: getTemplateText(content),
                        style: 'tableElement',
                        alignment: 'left',
                      },
                    ],
                  },
                ] as any[],
                _.map(report.columns, () => {
                  return {
                    image: checkboxType
                      ? checkBox
                      : radioType
                      ? radio
                      : textType
                      ? numericInput
                      : checkBox,
                    width: textType ? 35 : 7.5,
                    height: textType ? 17 : 7.5,
                    marginTop: textType ? 0 : 2,
                    style: 'tableElement',
                    alignment: 'center',
                  };
                })
              );
            })
          ),
        },
        layout: {
          hLineWidth: function(i: number, node: any) {
            return i === 0 || i === node.table.widths.length + 1 ? 0 : 0.25;
          },
          vLineWidth: function(i: number, _node: any) {
            return i === 1 ? 0.25 : 0;
          },
          hLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
          vLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
        },
      },
      '\n\n',
    ],
    report
  );
};

const getRatingOptions = (content: ExportData, index: number) => {
  const report = content.data as ListQuestionOptions;
  return getQuestionReport(
    index,
    content.text,
    [
      {
        style: 'table',
        table: {
          headerRows: 1,
          widths: ['*', '*'],
          body: [
            [
              {
                text: 'Scale',
                style: 'header',
              },
              {
                text: 'Variables',
                style: 'header',
              },
            ],
            [
              {
                stack: [
                  {
                    ol: _.map(report.columns, (c: any) => {
                      const content: TemplateContent = c.content;
                      return {
                        text: getTemplateText(content),
                        margin: [0, 3, 0, 3],
                      };
                    }),
                    style: 'tableElement',
                  },
                ],
              },
              {
                stack: [
                  {
                    ul: _.map(report.rows, (r: any) => {
                      const content: TemplateContent = r.content;
                      return {
                        text: getTemplateText(content),
                        margin: [0, 3, 0, 3],
                      };
                    }),
                    style: 'tableElement',
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function(i: number, node: any) {
            return i === 0 || i === node.table.widths.length + 2 ? 0 : 0.25;
          },
          vLineWidth: function(i: any, _node: any) {
            return i === 1 ? 0.25 : 0;
          },
          hLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
          vLineColor: function(_i: any, _node: any) {
            return 'lightgray';
          },
        },
      },
      '\n\n',
    ],
    report
  );
};

const getQuestionReport = (
  qIndex: number,
  text: string,
  content: {}[],
  report?: ListQuestionOptions
) => {
  const rtf = htmlToPdfMake(
    `<p>${text}</p>`.replace(new RegExp('\r?\n', 'g'), '<br />')
  );

  const updateDecorationToEmptyArray = (content: any) => {
    for (const element of content) {
      if (Array.isArray(element.text)) {
        updateDecorationToEmptyArray(element.text);
      } else if (element.decoration && element.decoration.includes('none')) {
        element.decoration = null;
      }
    }
  };

  updateDecorationToEmptyArray(rtf);

  return _.concat(
    [
      {
        text: `Question ${qIndex + 1}`,
        style: 'title',
        headlineLevel: 1,
      },
    ] as {}[],
    rtf,
    '\n',
    [
      {
        text: report ? report.helperText : '',
        style: 'helperText',
      },
    ],
    content
  );
};
